import { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, ScatterChart, Scatter, ReferenceLine
} from 'recharts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { Button, Input, Select, Textarea } from '../components/ui';

// #3 — promo events now come from commercial.PromoPerformance via
// /api/promo-performance (migration 035), replacing the hand-maintained
// promo-performance.json blob. The page also lets the team add/edit events.

const MECHANIC_COLORS = {
  '50% OFF':    '#ef4444',
  '2 FOR $7':   '#3b82f6',
  '2 FOR $6':   '#1d4ed8',
  '4 FOR $10':  '#f59e0b',
  'EDLP $5.75': '#22c55e',
};

const RETAILER_COLORS = { 'Woolworths': '#1a8a3f', 'Coles': '#e03131' };
const BRAND_COLORS = { 'Bhuja': '#f59e0b', 'Infuzions': '#3b82f6', 'Infuzions Multipack': '#8b5cf6' };

function pct(v) { return v != null ? `${v.toFixed(1)}%` : '—'; }
function fmtK(v) { return v != null ? `$${(v/1000).toFixed(0)}K` : '—'; }

export default function PromoEffectiveness() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const { data: events = [] } = useQuery({
    queryKey: ['promo-performance'],
    queryFn: () => apiFetch('/promo-performance').then((r) => r.json()),
  });
  const [retailer, setRetailer] = useState('Both');
  const [brand, setBrand] = useState('All');
  const [mechanic, setMechanic] = useState('All');
  const [sortCol, setSortCol] = useState('uplift_pct');
  const [sortDir, setSortDir] = useState('desc');
  const [editor, setEditor] = useState(null); // null | {} (new) | event (edit)

  const saveMutation = useMutation({
    mutationFn: async (ev) => {
      const body = JSON.stringify(ev);
      const res = ev.id
        ? await apiFetch(`/promo-performance/${ev.id}`, { method: 'PUT', body })
        : await apiFetch('/promo-performance', { method: 'POST', body });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Save failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['promo-performance'] }); setEditor(null); },
  });
  const deleteMutation = useMutation({
    mutationFn: async (id) => { const res = await apiFetch(`/promo-performance/${id}`, { method: 'DELETE' }); if (!res.ok) throw new Error('Delete failed'); return res.json(); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['promo-performance'] }); setEditor(null); },
  });

  const mechanics = ['All', ...Array.from(new Set(events.map(e => e.mechanic)))];
  const brands = ['All', ...Array.from(new Set(events.map(e => e.brand)))];

  const filtered = events.filter(e =>
    (retailer === 'Both' || e.retailer === retailer) &&
    (brand === 'All' || e.brand === brand) &&
    (mechanic === 'All' || e.mechanic === mechanic)
  );

  const sorted = [...filtered].sort((a, b) =>
    sortDir === 'desc' ? b[sortCol] - a[sortCol] : a[sortCol] - b[sortCol]
  );

  // KPIs
  const avgUplift = filtered.length ? filtered.reduce((s, e) => s + e.uplift_pct, 0) / filtered.length : null;
  const avgCM = filtered.length ? filtered.reduce((s, e) => s + e.majans_cm_pct, 0) / filtered.length : null;
  const totalPromoRSV = filtered.reduce((s, e) => s + e.promo_rsv, 0);
  const totalScanCost = filtered.reduce((s, e) => s + e.scan_deal_total, 0);
  const avgRetailerGP = filtered.length ? filtered.reduce((s, e) => s + e.retailer_gp_pct, 0) / filtered.length : null;

  // Uplift by mechanic (aggregated)
  const byMechanic = {};
  filtered.forEach(e => {
    if (!byMechanic[e.mechanic]) byMechanic[e.mechanic] = { mechanic: e.mechanic, events: 0, uplift_sum: 0, cm_sum: 0, rsv_sum: 0 };
    byMechanic[e.mechanic].events++;
    byMechanic[e.mechanic].uplift_sum += e.uplift_pct;
    byMechanic[e.mechanic].cm_sum += e.majans_cm_pct;
    byMechanic[e.mechanic].rsv_sum += e.promo_rsv;
  });
  const mechanicChart = Object.values(byMechanic).map(m => ({
    mechanic: m.mechanic,
    'Avg Uplift %': parseFloat((m.uplift_sum / m.events).toFixed(1)),
    'Avg CM %': parseFloat((m.cm_sum / m.events).toFixed(1)),
    events: m.events,
  })).sort((a, b) => b['Avg Uplift %'] - a['Avg Uplift %']);

  // Scatter: uplift vs CM
  const scatterData = filtered.map(e => ({
    x: e.uplift_pct,
    y: e.majans_cm_pct,
    name: `${e.retailer} ${e.brand} W${e.period_label}`,
    mechanic: e.mechanic,
    rsv: e.promo_rsv,
  }));

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => sortCol === col ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600 }}>Promotional Effectiveness</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Scan uplift, retailer GP, and Majans CM by mechanic — FY26
          </div>
        </div>
        <Button size="sm" variant="primary" onClick={() => setEditor({ retailer: 'Woolworths', brand: 'Bhuja', fy: 'FY26', mechanic: '50% OFF' })}>+ Add event</Button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Retailer</span>
        {['Both', 'Woolworths', 'Coles'].map(r => (
          <button key={r} className={retailer === r ? 'pill-active' : 'pill-inactive'} onClick={() => setRetailer(r)}>{r}</button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Brand</span>
        {brands.map(b => (
          <button key={b} className={brand === b ? 'pill-active' : 'pill-inactive'}
            style={{ fontSize: 11 }} onClick={() => setBrand(b)}>
            {b === 'Infuzions Multipack' ? 'Infuzions MP' : b}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Mechanic</span>
        {mechanics.map(m => (
          <button key={m} className={mechanic === m ? 'pill-active' : 'pill-inactive'}
            style={{ fontSize: 11 }} onClick={() => setMechanic(m)}>{m}</button>
        ))}
      </div>

      {/* KPI row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Avg Scan Uplift', value: avgUplift != null ? `+${avgUplift.toFixed(1)}%` : '—', color: '#22c55e', sub: `${filtered.length} events` },
          { label: 'Avg Majans CM%', value: avgCM != null ? `${avgCM.toFixed(1)}%` : '—', color: '#a855f7', sub: 'on promo volume' },
          { label: 'Avg Retailer GP%', value: avgRetailerGP != null ? `${avgRetailerGP.toFixed(1)}%` : '—', color: '#f59e0b', sub: 'WW ~28%, Coles ~27%' },
          { label: 'Total Promo RSV', value: fmtK(totalPromoRSV), color: '#3b82f6', sub: 'scan RSV during promo' },
          { label: 'Total Scan Deal Cost', value: fmtK(totalScanCost), color: '#ef4444', sub: 'Majans funded' },
        ].map(k => (
          <div key={k.label} className="kpi-card" style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
            {k.sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Uplift by mechanic */}
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>Avg Uplift % & CM% by Mechanic</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={mechanicChart} margin={{ left: 8, right: 8, top: 4, bottom: 4 }} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="mechanic" tick={{ fill: 'var(--text-body)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `${v}%`} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} width={36} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 11 }}
                formatter={(v, n) => [`${v}%`, n]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Avg Uplift %" radius={[2, 2, 0, 0]}>
                {mechanicChart.map((m, i) => <Cell key={i} fill={MECHANIC_COLORS[m.mechanic] || 'var(--text-muted)'} />)}
              </Bar>
              <Bar dataKey="Avg CM %" fill="#a855f7" radius={[2, 2, 0, 0]} opacity={0.8} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Uplift vs CM scatter */}
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Uplift % vs CM% — Event Scatter</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>Higher-right = strong uplift AND good margin</div>
          <ResponsiveContainer width="100%" height={220}>
            <ScatterChart margin={{ left: 4, right: 16, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis type="number" dataKey="x" name="Uplift %" tickFormatter={v => `${v}%`}
                tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} label={{ value: 'Uplift %', position: 'insideBottom', offset: -2, fill: 'var(--text-muted)', fontSize: 10 }} />
              <YAxis type="number" dataKey="y" name="CM %"
                tickFormatter={v => `${v}%`} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} width={36} />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 11 }}
                formatter={(v, n) => [`${v}%`, n]}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
              />
              <ReferenceLine x={avgUplift || 0} stroke="var(--text-muted)" strokeDasharray="3 2" />
              <ReferenceLine y={avgCM || 0} stroke="var(--text-muted)" strokeDasharray="3 2" />
              <Scatter data={scatterData} name="Events">
                {scatterData.map((d, i) => (
                  <Cell key={i} fill={MECHANIC_COLORS[d.mechanic] || 'var(--text-muted)'} opacity={0.85} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {Object.entries(MECHANIC_COLORS).map(([m, c]) => (
              <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-body)' }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
                {m}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Promo event table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Promotion Events</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Click column header to sort — {filtered.length} events</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Retailer</th>
                <th>Brand / Assortment</th>
                <th>Period</th>
                <th>Mechanic</th>
                <th>Activation</th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('promo_rsv')}>Promo RSV<SortIcon col="promo_rsv" /></th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('baseline_rsv')}>Baseline<SortIcon col="baseline_rsv" /></th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('uplift_pct')}>Uplift %<SortIcon col="uplift_pct" /></th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('retailer_gp_pct')}>Retailer GP%<SortIcon col="retailer_gp_pct" /></th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('majans_cm_pct')}>Majans CM%<SortIcon col="majans_cm_pct" /></th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('scan_deal_total')}>Scan Deal<SortIcon col="scan_deal_total" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(e => (
                <tr key={e.id} onClick={() => setEditor(e)} style={{ cursor: 'pointer' }} title="Click to edit">
                  <td style={{ color: RETAILER_COLORS[e.retailer] || 'var(--text-primary)', fontWeight: 500, fontSize: 12 }}>{e.retailer}</td>
                  <td>
                    <span style={{ color: BRAND_COLORS[e.brand] || 'var(--text-primary)', fontWeight: 500, fontSize: 12 }}>{e.brand}</span>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{e.assortment}</div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-body)' }}>{e.period_label}</td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 600, color: MECHANIC_COLORS[e.mechanic] || 'var(--text-body)',
                      background: 'var(--border)', borderRadius: 4, padding: '2px 6px' }}>
                      {e.mechanic}
                    </span>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 160 }}>{e.activation}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-primary)' }}>{fmtK(e.promo_rsv)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-body)' }}>{fmtK(e.baseline_rsv)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ color: e.uplift_pct >= 40 ? '#22c55e' : e.uplift_pct >= 20 ? '#f59e0b' : 'var(--text-body)', fontWeight: 600 }}>
                      +{e.uplift_pct.toFixed(0)}%
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', color: '#f59e0b' }}>{pct(e.retailer_gp_pct)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ color: e.majans_cm_pct >= 30 ? '#22c55e' : e.majans_cm_pct >= 20 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                      {pct(e.majans_cm_pct)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', color: '#ef4444' }}>{fmtK(e.scan_deal_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--text-muted)' }}>Uplift %</strong> — scan RSV vs estimated baseline (non-promo) for same period/store cohort. &nbsp;
          <strong style={{ color: 'var(--text-muted)' }}>Majans CM%</strong> — contribution margin on promotional volume after scan deal costs. Non-promo CM ~44–47%. &nbsp;
          <strong style={{ color: 'var(--text-muted)' }}>Retailer GP%</strong> — estimated retailer gross profit on promotional pricing (maintained via scan deal structure). &nbsp;
          Connect SALESv2/PLANAUDIT for exact CM values.
        </div>
      </div>

      {editor && (
        <PromoEventEditor
          initial={editor}
          onClose={() => setEditor(null)}
          onSave={(ev) => saveMutation.mutate(ev)}
          onDelete={(id) => deleteMutation.mutate(id)}
          saving={saveMutation.isPending || deleteMutation.isPending}
          error={saveMutation.error}
        />
      )}
    </div>
  );
}

const NUM_FIELDS = [
  ['promo_rsv', 'Promo RSV $'], ['baseline_rsv', 'Baseline RSV $'],
  ['uplift_pct', 'Uplift %'], ['retailer_gp_pct', 'Retailer GP %'],
  ['retailer_gp_value', 'Retailer GP $'], ['majans_cm_pct', 'Majans CM %'],
  ['majans_cm_value', 'Majans CM $'], ['scan_deal_total', 'Scan Deal $'],
];

function PromoEventEditor({ initial, onClose, onSave, onDelete, saving, error }) {
  const [form, setForm] = useState(() => ({ ...initial }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.retailer && form.brand;
  const submit = () => {
    if (!valid) return;
    const out = { ...form };
    NUM_FIELDS.forEach(([k]) => { out[k] = out[k] === '' || out[k] == null ? null : Number(out[k]); });
    onSave(out);
  };
  const lbl = { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(620px, 94vw)', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{form.id ? 'Edit promo event' : 'New promo event'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
          <div><div style={lbl}>Retailer</div><Select value={form.retailer || ''} onChange={set('retailer')}><option>Woolworths</option><option>Coles</option></Select></div>
          <div><div style={lbl}>Brand</div><Input value={form.brand || ''} onChange={set('brand')} placeholder="Bhuja, Infuzions…" /></div>
          <div><div style={lbl}>Assortment</div><Input value={form.assortment || ''} onChange={set('assortment')} placeholder="Bhuja 70-160g" /></div>
          <div><div style={lbl}>FY</div><Input value={form.fy || ''} onChange={set('fy')} placeholder="FY26" /></div>
          <div><div style={lbl}>Period label</div><Input value={form.period_label || ''} onChange={set('period_label')} placeholder="P4 W13" /></div>
          <div><div style={lbl}>Week commencing</div><Input type="date" value={form.week_commencing || ''} onChange={set('week_commencing')} /></div>
          <div><div style={lbl}>Mechanic</div><Input value={form.mechanic || ''} onChange={set('mechanic')} placeholder="50% OFF, 2 FOR $7…" /></div>
          <div><div style={lbl}>Activation</div><Input value={form.activation || ''} onChange={set('activation')} placeholder="Feature (813 stores)" /></div>
          {NUM_FIELDS.map(([k, label]) => (
            <div key={k}><div style={lbl}>{label}</div><Input type="number" step="any" value={form[k] ?? ''} onChange={set(k)} /></div>
          ))}
          <div style={{ gridColumn: '1 / -1' }}><div style={lbl}>Notes</div><Textarea rows={2} value={form.notes || ''} onChange={set('notes')} placeholder="FGE008, scan $0.48…" /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {error && <span style={{ fontSize: 12, color: 'var(--danger)', flex: 1 }}>{String(error.message ?? error)}</span>}
          {!error && form.id && <Button size="sm" variant="destructive" disabled={saving} onClick={() => onDelete(form.id)}>Delete</Button>}
          <span style={{ flex: error ? 0 : 1 }} />
          <Button size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="primary" disabled={saving || !valid} onClick={submit}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  );
}
